import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ImagePlus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { compressImage } from "@/lib/compressImage";

/**
 * Admin editor for the homepage "Featured Event" spotlight.
 * Controlled component: `value` is the featured_event object, `onChange` gets the full next object.
 */
export default function FeaturedEventAdmin({ value, onChange }) {
  const ev = value || {};
  const set = (patch) => onChange({ ...ev, ...patch });
  const listToText = (arr) => (arr || []).join("\n");
  const textToList = (txt) => txt.split("\n").map((x) => x.trim()).filter(Boolean);

  const onPoster = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 20 * 1024 * 1024) { toast.error("Poster must be under 20 MB"); return; }
    try {
      const { dataUrl } = await compressImage(f, { maxWidth: 1600, quality: 0.88 });
      set({ poster_image: dataUrl });
      toast.success("Poster added — remember to save");
    } catch { toast.error("Could not read that image"); }
  };

  return (
    <div className="hairline bg-zinc-900 p-5 space-y-4 mt-6" data-testid="featured-event-admin">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-display text-xl text-white tracking-wider">FEATURED EVENT · HOMEPAGE SPOTLIGHT</h3>
          <p className="font-mono text-[10px] text-zinc-500 uppercase tracking-widest mt-1">
            Big banner right under the hero. Auto-hides the day after the event date.
          </p>
        </div>
        <label className="flex items-center gap-2 shrink-0 cursor-pointer">
          <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-400">
            {ev.enabled ? "LIVE" : "HIDDEN"}
          </span>
          <Switch
            data-testid="featured-event-enabled"
            checked={!!ev.enabled}
            onCheckedChange={(v) => set({ enabled: v })}
            className="data-[state=checked]:bg-orange-600"
          />
        </label>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <F label="EYEBROW (SMALL LABEL)" dt="fe-eyebrow" value={ev.eyebrow} onChange={(v) => set({ eyebrow: v })} placeholder="NEXT UP · FLAGSHIP EVENT" />
        <F label="EVENT TITLE" dt="fe-title" value={ev.title} onChange={(v) => set({ title: v })} placeholder="LATENT 5K" />
      </div>
      <F label="TAGLINE" dt="fe-tagline" value={ev.tagline} onChange={(v) => set({ tagline: v })} placeholder="Unlock the runner hidden inside you." />
      <div>
        <Label className="font-display tracking-widest text-xs text-zinc-400">DESCRIPTION</Label>
        <Textarea
          data-testid="fe-description"
          value={ev.description || ""}
          onChange={(e) => set({ description: e.target.value })}
          rows={3}
          className="mt-2 rounded-none bg-zinc-950 border-zinc-800 text-white focus-visible:ring-orange-600"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <F label="DATE" dt="fe-date" type="date" value={ev.date} onChange={(v) => set({ date: v })} />
        <F label="FLAG-OFF TIME" dt="fe-time" value={ev.time} onChange={(v) => set({ time: v })} placeholder="5:30 AM" />
        <F label="VENUE" dt="fe-location" value={ev.location} onChange={(v) => set({ location: v })} placeholder="Rourkela, Odisha" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="font-display tracking-widest text-xs text-zinc-400">DISTANCES · ONE PER LINE</Label>
          <Textarea
            data-testid="fe-distances"
            defaultValue={listToText(ev.distances)}
            onBlur={(e) => set({ distances: textToList(e.target.value) })}
            rows={3}
            placeholder={"5K RUN\n5K WALK"}
            className="mt-2 rounded-none bg-zinc-950 border-zinc-800 text-white font-mono text-sm focus-visible:ring-orange-600"
          />
        </div>
        <div>
          <Label className="font-display tracking-widest text-xs text-zinc-400">HIGHLIGHTS · ONE PER LINE (MAX 4 SHOWN)</Label>
          <Textarea
            data-testid="fe-highlights"
            defaultValue={listToText(ev.highlights)}
            onBlur={(e) => set({ highlights: textToList(e.target.value) })}
            rows={3}
            placeholder={"Finisher medal\nTimed bib\nRefreshments"}
            className="mt-2 rounded-none bg-zinc-950 border-zinc-800 text-white font-mono text-sm focus-visible:ring-orange-600"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <F label="ENTRY FEE (OPTIONAL)" dt="fe-fee" value={ev.entry_fee} onChange={(v) => set({ entry_fee: v })} placeholder="₹499 · Early bird ₹399" />
        <F label="REGISTRATION LINK (BLANK = OSCR SIGN-UP)" dt="fe-register-url" value={ev.register_url} onChange={(v) => set({ register_url: v })} placeholder="https://forms.gle/… or ticketing link" />
      </div>

      {/* Poster */}
      <div>
        <Label className="font-display tracking-widest text-xs text-zinc-400">EVENT POSTER (PORTRAIT 4:5 RECOMMENDED)</Label>
        <div className="mt-2 flex items-center gap-4">
          {ev.poster_image ? (
            <div className="relative shrink-0">
              <img src={ev.poster_image} alt="" className="w-20 h-24 object-cover hairline border-zinc-800" data-testid="fe-poster-preview" />
              <button
                type="button"
                data-testid="fe-poster-remove"
                onClick={() => set({ poster_image: "" })}
                className="absolute -top-2 -right-2 bg-zinc-950 border border-zinc-700 p-1 text-zinc-400 hover:text-red-500"
              >
                <Trash2 size={12} />
              </button>
            </div>
          ) : (
            <div className="w-20 h-24 hairline border-dashed border-zinc-700 flex items-center justify-center text-zinc-600 font-mono text-[9px] tracking-widest shrink-0">
              NO POSTER
            </div>
          )}
          <div className="flex-1 space-y-2">
            <input id="fe-poster-file" data-testid="fe-poster-file" type="file" accept="image/*" className="hidden" onChange={onPoster} />
            <label
              htmlFor="fe-poster-file"
              className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 hairline bg-orange-600 border-orange-500 text-white font-display tracking-wider text-xs hover:bg-orange-500 transition"
            >
              <ImagePlus size={14} /> UPLOAD POSTER
            </label>
            <Input
              data-testid="fe-poster-url"
              value={/^data:/.test(ev.poster_image || "") ? "" : ev.poster_image || ""}
              onChange={(e) => set({ poster_image: e.target.value })}
              placeholder="…or paste an image URL"
              className="rounded-none bg-zinc-950 border-zinc-800 text-white font-mono text-xs focus-visible:ring-orange-600"
            />
          </div>
        </div>
        <p className="font-mono text-[9px] text-zinc-500 mt-2 tracking-widest uppercase">
          No poster? A designed OSCR placeholder card is shown automatically.
        </p>
      </div>
    </div>
  );
}

function F({ label, value, onChange, dt, placeholder, type = "text" }) {
  return (
    <div>
      <Label className="font-display tracking-widest text-xs text-zinc-400">{label}</Label>
      <Input
        data-testid={dt}
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-2 rounded-none bg-zinc-950 border-zinc-800 text-white focus-visible:ring-orange-600"
      />
    </div>
  );
}
